import { Maximize2, Plus, RefreshCw } from 'lucide-react';
import {
  BREAKPOINT_COLS,
  WIDGET_CLASS_CONSTRAINTS,
} from '@aimsos/ui';
import { DEFAULT_WIDGET_H, DEFAULT_WIDGET_W } from '../constants';

const ACTION_MAP = {
  refresh: {
    key: 'BTN_REFRESH',
    icon: RefreshCw,
    tooltip: 'Update',
    action: { type: 'API' },
  },
  expand: {
    key: 'BTN_EXPAND',
    icon: Maximize2,
    tooltip: 'View all',
    action: { type: 'NAVIGATE' },
  },
  add: {
    key: 'BTN_ADD',
    icon: Plus,
    tooltip: 'Add',
    action: { type: 'SLIDEOUT' },
  },
};

const DEFAULT_CONSTRAINTS = WIDGET_CLASS_CONSTRAINTS.CLASS_2_STANDARD;
const GRID_COLS = BREAKPOINT_COLS.lg;

const toHeaderButtons = (actions) => {
  const actionMap = ACTION_MAP;
  if (!Array.isArray(actions)) return [];
  return actions.map((action) => actionMap[action]).filter(Boolean);
};

const toConstraints = (widgetClass) => WIDGET_CLASS_CONSTRAINTS[widgetClass] || DEFAULT_CONSTRAINTS;

const resolveDefaultDimensions = (constraints) => {
  const defaultW = Math.max(DEFAULT_WIDGET_W, constraints.minW || DEFAULT_WIDGET_W);
  const classMaxH = Number.isFinite(constraints.maxH)
    ? constraints.maxH
    : (constraints.minH || DEFAULT_WIDGET_H);
  const defaultH = Math.max(DEFAULT_WIDGET_H, classMaxH);
  return { defaultW, defaultH };
};

const hasCollision = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const clampWidth = (width, constraints, fallbackWidth) => {
  const min = constraints.minW || fallbackWidth;
  const max = constraints.maxW || fallbackWidth;
  const safeMin = Math.min(min, max);
  const safeMax = Math.max(min, max);
  return Math.min(Math.max(width ?? fallbackWidth, safeMin), safeMax);
};

const clampHeight = (height, constraints, fallbackHeight) => {
  const min = constraints.minH || fallbackHeight;
  const max = Number.isFinite(constraints.maxH) ? constraints.maxH : fallbackHeight;
  const safeMin = Math.min(min, max);
  const safeMax = Math.max(min, max);
  return Math.min(Math.max(height ?? fallbackHeight, safeMin), safeMax);
};

const findNextFreePosition = (placedRects, widgetW, widgetH, cols = GRID_COLS) => {
  const boundedWidgetW = Math.min(widgetW, cols);
  const maxX = Math.max(0, cols - boundedWidgetW);
  let y = 0;

  while (y < 10000) {
    for (let x = 0; x <= maxX; x += 1) {
      const candidate = { x, y, w: boundedWidgetW, h: widgetH };
      const collides = placedRects.some((rect) => hasCollision(candidate, rect));
      if (!collides) return { x, y };
    }
    y += 1;
  }

  const maxBottomY = placedRects.reduce((max, rect) => Math.max(max, rect.y + rect.h), 0);
  return { x: 0, y: maxBottomY };
};

const expandLastWidgetPerRow = (layout, cols = GRID_COLS) => {
  if (!Array.isArray(layout) || layout.length === 0) return [];

  const expanded = layout.map((item) => ({ ...item }));
  const rows = new Map();

  expanded.forEach((item) => {
    const key = item.y;
    if (!rows.has(key)) rows.set(key, []);
    rows.get(key).push(item);
  });

  rows.forEach((rowItems) => {
    const sorted = [...rowItems].sort((a, b) => a.x - b.x);
    const last = sorted[sorted.length - 1];
    const maxW = Number.isFinite(last.maxW) ? last.maxW : cols;
    const available = Math.max(1, cols - last.x);
    last.w = Math.min(Math.max(last.w, available), maxW);
  });

  return expanded;
};

const buildStackedLayout = (layoutItems, cols) => {
  if (!Array.isArray(layoutItems) || layoutItems.length === 0) return [];

  const sorted = [...layoutItems].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  let currentY = 0;

  return sorted.map((item) => {
    const stacked = { ...item, x: 0, w: cols, y: currentY };
    currentY += item.h;
    return stacked;
  });
};

const adaptWidget = (widget, index, placedRects) => {
  const instanceUuid = widget.instanceId || widget.instance_uuid || `${Date.now()}-${widget.type}-${index}`;
  const widgetType = widget.type || widget.widgetType;
  const actions = widget.defaultActions || widget.actions;
  const constraints = toConstraints(widget.widgetClass);
  const { defaultW, defaultH } = resolveDefaultDimensions(constraints);
  const boundedDefaultW = Math.min(defaultW, GRID_COLS);

  const layout = widget.layout
    ? {
        ...widget.layout,
        i: widget.layout.i || instanceUuid,
        x: Number.isFinite(widget.layout.x) ? widget.layout.x : 0,
        y: Number.isFinite(widget.layout.y) ? widget.layout.y : 0,
        w: clampWidth(widget.layout.w, constraints, boundedDefaultW),
        h: clampHeight(widget.layout.h, constraints, defaultH),
      }
    : {
        i: instanceUuid,
        ...findNextFreePosition(placedRects, boundedDefaultW, defaultH, GRID_COLS),
        w: boundedDefaultW,
        h: defaultH,
      };

  return {
    instance_uuid: instanceUuid,
    widget_definition_uuid: widget.id || `builder-${widgetType || 'widget'}-def`,
    widget_type: 'CUSTOM_FIXED',
    accepts_global_filters: false,
    layout,
    constraints,
    config: {
      card: {
        title: widget.name || widget.title || '',
        description: widget.description || '',
        header_buttons: toHeaderButtons(actions),
      },
    },
    _builderType: widgetType,
    // The catalog widget this placement pins, so the canvas can execute it.
    // Null for the seeded widgets: they point at no stored definition, and
    // `widget_definition_uuid` above invents one for them.
    _widgetUuid: widget.widgetUuid ?? null,
    _locked: widget.locked ?? false,
  };
};

const toLayoutItem = (widget) => ({
  i: widget.instance_uuid,
  x: widget.layout.x,
  y: widget.layout.y,
  w: widget.layout.w,
  h: widget.layout.h,
  ...widget.constraints,
});

const buildResponsiveLayouts = (lgLayout) => {
  if (!lgLayout?.length) return {};

  const expandedLg = expandLastWidgetPerRow(lgLayout, BREAKPOINT_COLS.lg);

  return {
    lg: expandedLg,
    md: expandedLg,
    sm: expandedLg,
    xs: buildStackedLayout(expandedLg, BREAKPOINT_COLS.xs),
    xxs: buildStackedLayout(expandedLg, BREAKPOINT_COLS.xxs),
  };
};

export const adaptWidgetsForGrid = (editorWidgets = [], options = {}) => {
  const {
    tooltips = {},
  } = options;

  if (tooltips.refresh || tooltips.expand || tooltips.add) {
    ACTION_MAP.refresh.tooltip = tooltips.refresh || ACTION_MAP.refresh.tooltip;
    ACTION_MAP.expand.tooltip = tooltips.expand || ACTION_MAP.expand.tooltip;
    ACTION_MAP.add.tooltip = tooltips.add || ACTION_MAP.add.tooltip;
  }

  if (!Array.isArray(editorWidgets) || editorWidgets.length === 0) {
    return { widgets: [], layouts: {}, gridConfig: null };
  }

  const placedRects = [];
  const widgets = editorWidgets.map((widget, index) => {
    const adapted = adaptWidget(widget, index, placedRects);
    placedRects.push({
      x: adapted.layout.x,
      y: adapted.layout.y,
      w: adapted.layout.w,
      h: adapted.layout.h,
    });
    return adapted;
  });
  const lgLayout = widgets.map(toLayoutItem);
  const layouts = buildResponsiveLayouts(lgLayout);

  return {
    widgets,
    layouts,
    // No `cols`: twelve columns is already `WidgetGrid`'s default, and the
    // bare number this used to send (`cols: 12`) is not the per-breakpoint
    // object the grid wants. It sat here inert until the grid started reading
    // the field, and then took every dashboard with a widget to a blank page.
    gridConfig: { rowHeight: 24 },
  };
};
